package br.com.duoma.duomaproducao.ui.components

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import br.com.duoma.duomaproducao.model.WorkCenter
import br.com.duoma.duomaproducao.ui.misc.mediaQuery
import com.patrykandpatrick.vico.compose.cartesian.CartesianChartHost
import com.patrykandpatrick.vico.compose.cartesian.Scroll
import com.patrykandpatrick.vico.compose.cartesian.axis.HorizontalAxis
import com.patrykandpatrick.vico.compose.cartesian.axis.VerticalAxis
import com.patrykandpatrick.vico.compose.cartesian.data.CartesianChartModelProducer
import com.patrykandpatrick.vico.compose.cartesian.data.CartesianLayerRangeProvider
import com.patrykandpatrick.vico.compose.cartesian.data.CartesianValueFormatter
import com.patrykandpatrick.vico.compose.cartesian.layer.ColumnCartesianLayer
import com.patrykandpatrick.vico.compose.cartesian.layer.rememberColumnCartesianLayer
import com.patrykandpatrick.vico.compose.cartesian.marker.CartesianMarker
import com.patrykandpatrick.vico.compose.cartesian.marker.CartesianMarkerController
import com.patrykandpatrick.vico.compose.cartesian.marker.CartesianMarkerVisibilityListener
import com.patrykandpatrick.vico.compose.cartesian.marker.ColumnCartesianLayerMarkerTarget
import com.patrykandpatrick.vico.compose.cartesian.marker.DefaultCartesianMarker
import com.patrykandpatrick.vico.compose.cartesian.marker.rememberDefaultCartesianMarker
import com.patrykandpatrick.vico.compose.cartesian.rememberCartesianChart
import com.patrykandpatrick.vico.compose.cartesian.rememberVicoScrollState
import com.patrykandpatrick.vico.compose.cartesian.rememberVicoZoomState
import com.patrykandpatrick.vico.compose.common.Fill
import com.patrykandpatrick.vico.compose.common.component.LineComponent
import com.patrykandpatrick.vico.compose.common.component.rememberTextComponent
import kotlin.math.roundToInt

/**
 * Generic column bar chart.
 *
 * @param model       the Vico model producer
 * @param xLabels     label for each bar on the x-axis; defaults to WorkCenter names
 * @param scrollable  whether to enable horizontal scrolling (and start at the end)
 * @param modifier    layout modifier applied to the wrapping Card
 */
@Composable
fun WorkCenterBarChart(
    model: CartesianChartModelProducer,
    modifier: Modifier = Modifier,
    xLabels: List<String> = WorkCenter.entries.map { it.toString() },
    xLabelsLinecount : Int = 1,
    scrollable: Boolean = false,
    columnWidth : Dp = mediaQuery(42.dp, 26.dp),
    rangeProvider: CartesianLayerRangeProvider = remember { CartesianLayerRangeProvider.auto() },
    filter : (@Composable () -> Unit)? = null,
    onColumnClick: ((Int) -> Unit)? = null,
) {
    val colorScheme = MaterialTheme.colorScheme
    val typography = MaterialTheme.typography
    val columnWidth = if (scrollable) 32.dp else 42.dp
    val currentOnColumnClick = rememberUpdatedState(onColumnClick)
    val clickMarker = if (onColumnClick != null) {
        rememberDefaultCartesianMarker(
            label = rememberTextComponent(
                typography.labelSmall.copy(
                    fontWeight = FontWeight.Bold,
                    color = colorScheme.onSurface,
                )
            ),
            valueFormatter = DefaultCartesianMarker.ValueFormatter { _, targets ->
                val quantity = targets
                    .filterIsInstance<ColumnCartesianLayerMarkerTarget>()
                    .firstOrNull()
                    ?.columns
                    ?.sumOf { it.entry.y }
                    ?.roundToInt()
                quantity?.toString() ?: "-"
            },
            labelPosition = DefaultCartesianMarker.LabelPosition.AbovePoint,
            guideline = LineComponent(Fill(colorScheme.outlineVariant), 1.dp),
        )
    } else {
        null
    }
    val clickMarkerListener = remember {
        object : CartesianMarkerVisibilityListener {
            override fun onShown(marker: CartesianMarker, targets: List<CartesianMarker.Target>) {
                notifyColumnClick(targets)
            }

            override fun onUpdated(marker: CartesianMarker, targets: List<CartesianMarker.Target>) {
                notifyColumnClick(targets)
            }

            private fun notifyColumnClick(targets: List<CartesianMarker.Target>) {
                val index = targets
                    .filterIsInstance<ColumnCartesianLayerMarkerTarget>()
                    .firstOrNull()
                    ?.x
                    ?.roundToInt()
                    ?: return
                currentOnColumnClick.value?.invoke(index)
            }
        }
    }
    Card(
        modifier = modifier,
        elevation = CardDefaults.elevatedCardElevation(defaultElevation = 2.dp),
        colors = CardDefaults.cardColors(containerColor = colorScheme.surface),
        shape = RoundedCornerShape(12.dp),
    ) {
        if (filter != null)
            Column(
                Modifier
                    .fillMaxWidth()
                    .padding(
                        top = 16.dp,
                        bottom = 12.dp,
                        start = mediaQuery(0.dp, 16.dp),
                        end = 16.dp,
                    ),
                horizontalAlignment = mediaQuery(
                    Alignment.End,
                    Alignment.CenterHorizontally
                ),
            ) { filter() }
        Column {
            CartesianChartHost(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(
                        top = if (filter == null) mediaQuery(
                            30.dp,
                            16.dp
                        ) else 0.dp,
                        bottom = 12.dp,
                        start = 8.dp,
                        end = mediaQuery(16.dp, 8.dp),
                    ),
                scrollState = rememberVicoScrollState(
                    scrollEnabled = scrollable,
                    initialScroll = if (scrollable) Scroll.Absolute.End else Scroll.Absolute.Start,
                ),
                zoomState = rememberVicoZoomState(zoomEnabled = false),
                chart = rememberCartesianChart(
                    rememberColumnCartesianLayer(
                        columnProvider = ColumnCartesianLayer.ColumnProvider.series(
                            LineComponent(
                                Fill(colorScheme.primary),
                                columnWidth,
                                RoundedCornerShape(topStart = 6.dp, topEnd = 6.dp),
                            ),
                        ),
                        dataLabel = rememberTextComponent(
                            typography.labelMedium.copy(
                                fontWeight = FontWeight.Bold,
                                color = colorScheme.onSurfaceVariant,
                            ),
                        ),
                        dataLabelValueFormatter = CartesianValueFormatter { _, value, _ ->
                            try {
                                value.toLong().toString()
                            } catch (_ : Exception) {
                                "-"
                            }
                        },
                        rangeProvider = rangeProvider,
                    ),
                    startAxis = VerticalAxis.rememberStart(
                        valueFormatter = CartesianValueFormatter { _, value, _ ->
                            try { value.toLong().toString() } catch (_: Exception) { "0" }
                        },
                        label = rememberTextComponent(
                            typography.labelSmall.copy(color = colorScheme.onSurfaceVariant),
                        ),
                        guideline = LineComponent(Fill(colorScheme.outlineVariant), 1.dp),
                        line = LineComponent(Fill(colorScheme.outline), 1.dp),
                    ),
                    bottomAxis = HorizontalAxis.rememberBottom(
                        valueFormatter = CartesianValueFormatter { _, value, _ ->
                            try {
                                xLabels.getOrElse(value.toInt()) { "-" }.ifBlank { "-" }
                            } catch (_ : Exception) {
                                "-"
                            }
                        },
                        guideline = null,
                        label = rememberTextComponent(
                            typography.labelMedium.copy(
                                fontWeight = FontWeight.SemiBold,
                                color = colorScheme.onSurface,
                                textAlign = TextAlign.Center,
                            ),
                            lineCount = xLabelsLinecount,
                        ),
                        line = LineComponent(Fill(colorScheme.outline), 1.dp),
                    ),
                    marker = clickMarker,
                    markerVisibilityListener = if (onColumnClick != null) clickMarkerListener else null,
                    markerController = CartesianMarkerController.rememberToggleOnTap(),
                ),
                modelProducer = model,
                placeholder = { Box(Modifier.fillMaxSize()) },
            )
        }
    }
}
